import {
  Autocomplete,
  Button,
  Divider,
  Flex,
  Grid,
  Label,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";

import useErrors from "hooks/useErrors";
import UsersChoose from "components/users/UsersChoose";
import PermissionsChoose from "components/permissions/PermissionsChoose";
import { listCompanies } from "graphql/queries";
import { LayoutContext } from "layout/context/LayoutContext";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";

const RoleForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [roleForm, setRoleForm] = useState(initialValues);
  const [resetValues, setResetValues] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    description: "",
    companyID: "",
  });
  const validateErrors = useErrors({
    name: "isRequired",
    companyID: me.companyID && "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(roleForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (
      roleForm.companyID &&
      !companies.some((companyRec) => companyRec.id === roleForm.companyID)
    ) {
      setErrors((prevSt) => ({
        ...prevSt,
        companyID: strings.validations.incorrectValue,
      }));
      return;
    }

    if (!hasErrors)
      onSubmit({
        ...roleForm,
        companyID: roleForm.companyID || null,
      });
  };

  const onClearSelect = () => {
    const value = "";
    fetchCompanies({ name: value });
    setRoleForm((prevSt) => ({
      ...prevSt,
      companyID: value,
    }));
  };

  const onSelect = (name, value) => {
    setRoleForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    fetchCompanies({ name: value });
    setRoleForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setRoleForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeUsers = (users) => {
    setRoleForm((prevSt) => ({
      ...prevSt,
      users,
    }));
  };

  const onChangePermissions = (permissions) => {
    setRoleForm((prevSt) => ({
      ...prevSt,
      permissions,
    }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      name: "",
      description: "",
      companyID: "",
    });
    setRoleForm(initialValues);
    setResetValues((prev) => !prev);
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompaniesLoading(true);

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies(companiesItems);
      setCompaniesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    }
  }

  useEffect(() => {
    setRoleForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Grid rowGap="15px" columnGap="15px" padding="20px">
      <TextField
        name="name"
        label={
          <span>
            {strings.fields.name.label}
            <span style={{ fontSize: "0.8rem", color: "red" }}>{" *"}</span>
          </span>
        }
        value={roleForm.name}
        onChange={onChange}
        errorMessage={errors.name}
        hasError={!!errors.name}
      ></TextField>
      <TextAreaField
        name="description"
        label={strings.fields.description.label}
        value={roleForm.description}
        onChange={onChange}
        errorMessage={errors.description}
        hasError={!!errors.description}
      ></TextAreaField>
      {type === "create" && (
        <>
          <Label htmlFor="companyID">
            {<LabelRequired text={strings.fields.company.label} />}
          </Label>
          <div style={{ marginTop: "-5px" }}>
            <Autocomplete
              id="companyID"
              name="companyID"
              label={<LabelRequired text={strings.fields.company.label} />}
              isRequired
              placeholder={strings.fields.company.placeholder}
              value={
                companies.find(
                  (companyRec) => companyRec.id === roleForm.companyID
                )?.name ??
                roleForm.companyID ??
                ""
              }
              options={companies.map((companyRec) => ({
                id: companyRec.id,
                label: companyRec.name,
              }))}
              isLoading={companiesLoading}
              onClear={() => onClearSelect("companyID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("companyID", id)}
              errorMessage={errors?.companyID}
              hasError={!!errors?.companyID}
              menuSlots={{ Empty: globalStrings.noResults }}
            ></Autocomplete>
          </div>
        </>
      )}
      <Divider className="mt-3 mb-2" />
      <PermissionsChoose
        initPermissionsGranted={initialValues.permissions}
        onChangePermissionsGranted={onChangePermissions}
        resetValues={resetValues}
      />
      <Divider className="mt-3 mb-2" />
      <UsersChoose
        initUsersGranted={initialValues.users}
        onChangeUsersGranted={onChangeUsers}
        resetValues={resetValues}
      />
      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={resetFormValues}
        ></Button>
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
          isDisabled={Object.values(errors).some((e) => !!e)}
        ></Button>
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
    },
    fields: {
      name: {
        label: "Nombre",
      },
      description: {
        label: "Descripción",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
  },
});

export default RoleForm;
